import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Alert,
    IconButton,
    InputAdornment,
    CircularProgress,
    Typography
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Lock as LockIcon
} from '@mui/icons-material';
import authService from '../services/auth.service';

interface ChangePasswordProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

interface PasswordErrors {
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
}

const ChangePassword: React.FC<ChangePasswordProps> = ({ open, onClose, onSuccess }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [errors, setErrors] = useState<PasswordErrors>({});

    const validateForm = (): boolean => {
        const newErrors: PasswordErrors = {};

        if (!currentPassword) {
            newErrors.currentPassword = 'Current password is required';
        }

        if (!newPassword) {
            newErrors.newPassword = 'New password is required';
        } else if (newPassword.length < 6) {
            newErrors.newPassword = 'Password must be at least 6 characters';
        } else if (newPassword === currentPassword) {
            newErrors.newPassword = 'New password must be different from current password';
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await authService.changePassword(currentPassword, newPassword);
            setSuccess(true);
            setTimeout(() => {
                handleClose();
                if (onSuccess) {
                    onSuccess();
                }
            }, 2000);
        } catch (err: any) {
            console.error('Change password error:', err);
            if (err.response?.data?.error) {
                setError(err.response.data.error);
            } else {
                setError('Failed to change password. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setError(null);
        setSuccess(false);
        setErrors({});
        onClose();
    };

    const getPasswordStrength = (password: string): { strength: string; color: string } => {
        if (!password) return { strength: '', color: '' };
        
        let strength = 0;
        if (password.length >= 8) strength++;
        if (password.length >= 12) strength++;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
        if (/[0-9]/.test(password)) strength++;
        if (/[^A-Za-z0-9]/.test(password)) strength++;

        if (strength <= 1) return { strength: 'Weak', color: 'error' };
        if (strength <= 3) return { strength: 'Medium', color: 'warning' };
        return { strength: 'Strong', color: 'success' };
    };

    const passwordStrength = getPasswordStrength(newPassword);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <LockIcon />
                        Change Password
                    </Box>
                </DialogTitle>
                
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}
                        
                        {success && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                Password changed successfully!
                            </Alert>
                        )}

                        <TextField
                            fullWidth
                            label="Current Password"
                            type={showCurrentPassword ? 'text' : 'password'}
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            error={!!errors.currentPassword}
                            helperText={errors.currentPassword}
                            margin="normal"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                            edge="end"
                                        >
                                            {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            disabled={loading || success}
                        />

                        <TextField
                            fullWidth
                            label="New Password"
                            type={showNewPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            error={!!errors.newPassword}
                            helperText={errors.newPassword}
                            margin="normal"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            edge="end"
                                        >
                                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            disabled={loading || success}
                        />

                        {newPassword && (
                            <Box sx={{ mt: 1, mb: 1 }}>
                                <Typography variant="caption" color="textSecondary">
                                    Password strength:{' '}
                                    <Typography
                                        component="span"
                                        variant="caption"
                                        color={`${passwordStrength.color}.main`}
                                        fontWeight="bold"
                                    >
                                        {passwordStrength.strength}
                                    </Typography>
                                </Typography>
                            </Box>
                        )}

                        <TextField
                            fullWidth
                            label="Confirm New Password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword}
                            margin="normal"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            disabled={loading || success}
                        />

                        <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" color="textSecondary">
                                Password requirements:
                            </Typography>
                            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                                <li>
                                    <Typography variant="caption" color="textSecondary">
                                        At least 6 characters long
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="caption" color="textSecondary">
                                        Different from your current password
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="caption" color="textSecondary">
                                        Recommended: Use a mix of letters, numbers, and symbols
                                    </Typography>
                                </li>
                            </ul>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || success}
                        startIcon={loading && <CircularProgress size={20} />}
                    >
                        {loading ? 'Changing...' : 'Change Password'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ChangePassword;